import { Injectable, inject, signal, computed } from '@angular/core';
import { AlertStatisticsApi } from '../api/alert-statistics.api';
import { AlertStatistics } from '../models/classes/alert';

@Injectable({ providedIn: 'root' })
export class AlertStatisticsStore {
  private alertStatisticsApi = inject(AlertStatisticsApi);

  // ==================== Private Mutable Signals ====================
  private _loading = signal(false);
  private _alertStatistics = signal<AlertStatistics | null>(null);
  private _lastUpdated = signal<Date | null>(null);
  private _error = signal<string | null>(null);

  // ==================== Public Readonly Signals ====================
  readonly loading = this._loading.asReadonly();
  readonly alertStatistics = this._alertStatistics.asReadonly();
  readonly lastUpdated = this._lastUpdated.asReadonly();
  readonly error = this._error.asReadonly();

  // ==================== Computed Signals ====================
  readonly hasStatistics = computed(() => this._alertStatistics() !== null);
  readonly criticalObjectsCount = computed(() => this._alertStatistics()?.criticalObjects ?? 0);
  readonly criticalSensorsCount = computed(() => this._alertStatistics()?.criticalSensors ?? 0);
  readonly alertsCount = computed(() => this._alertStatistics()?.alerts ?? 0);
  readonly notificationsCount = computed(() => this._alertStatistics()?.notifications ?? 0);

  readonly hasCriticalObjects = computed(() => this.criticalObjectsCount() > 0);
  readonly hasCriticalSensors = computed(() => this.criticalSensorsCount() > 0);
  readonly hasAlerts = computed(() => this.alertsCount() > 0);
  readonly hasNotifications = computed(() => this.notificationsCount() > 0);

  // ==================== Operations ====================

  /**
   * Fetch and update alert statistics
   * @returns Promise with alert statistics
   */
  async fetchAlertStatistics(): Promise<AlertStatistics> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const result = await this.alertStatisticsApi.getAlertStatistics();
      this._alertStatistics.set(result);
      this._lastUpdated.set(new Date());
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch alert statistics';
      this._error.set(errorMessage);
      console.error('Failed to fetch alert statistics:', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Get current statistics (synchronous)
   * @returns Current alert statistics or null
   */
  getCurrentStatistics(): AlertStatistics | null {
    return this._alertStatistics();
  }

  clear(): void {
    this._alertStatistics.set(null);
    this._lastUpdated.set(null);
    this._error.set(null);
    this._loading.set(false);
  }

  clearError(): void {
    this._error.set(null);
  }
}
